import { CheckCircle2 } from "lucide-react";
import { Reveal } from "@/components/campaign/Reveal";

const CONCERNS = [
  {
    title: "Fatigue that rest doesn\u2019t fix",
    why: "Persistent tiredness can have several contributing factors worth evaluating together.",
  },
  {
    title: "Digestive issues (bloating, irregularity)",
    why: "Digestion often reflects diet, stress, sleep and other systems working together.",
  },
  {
    title: "Weight or body-composition changes",
    why: "An InBody assessment gives a factual starting point that goes beyond the scale.",
  },
  {
    title: "Brain fog or trouble concentrating",
    why: "Sleep, stress, nutrition and hormones may each play a part worth discussing.",
  },
  {
    title: "Sleep changes",
    why: "How you sleep affects nearly everything else you want to improve.",
  },
  {
    title: "Thyroid questions or \u201cnormal\u201d labs with ongoing symptoms",
    why: "Results can sit in range while symptoms still deserve a closer look.",
  },
  {
    title: "Hormone questions (men and women)",
    why: "A full history helps decide whether testing would answer a useful question.",
  },
  {
    title: "Wanting a prevention-focused, whole-person plan",
    why: "You don\u2019t need to be sick to want a clear, proactive baseline.",
  },
];

export const FmConcerns = ({ onRequest }) => (
  <section id="concerns" data-testid="fm-concerns-section" className="scroll-mt-20 py-16 sm:py-24">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <div className="grid lg:grid-cols-[1fr_1.6fr] gap-10 lg:gap-14 items-start">
        <Reveal>
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-[var(--brand-gold)]">
            Concerns patients often bring
          </p>
          <h2 className="mt-3 font-serif-brand text-3xl sm:text-4xl font-medium text-[var(--brand-navy)] leading-tight">
            Bring the concern. We&rsquo;ll look at it closely, together.
          </h2>
          <div className="relative mt-8 hidden lg:block">
            <div className="absolute -inset-2 border border-[var(--brand-gold)] opacity-50" aria-hidden="true" />
            <img
              src="/images/fm/lab-review.webp"
              alt="A clinician handing a patient their results during a visit."
              className="relative w-full h-72 object-cover shadow-lg"
              loading="lazy"
            />
          </div>
        </Reveal>
        <div>
          <div className="grid sm:grid-cols-2 gap-4">
            {CONCERNS.map((item, i) => (
              <Reveal key={item.title} delay={(i % 4) * 0.08}>
                <div
                  data-testid={`fm-concern-card-${i + 1}`}
                  className="h-full bg-white border border-[var(--brand-gold-soft)] p-5"
                >
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-[var(--brand-gold)]" aria-hidden="true" />
                    <div>
                      <h3 className="font-semibold text-[var(--brand-navy)] leading-snug">{item.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-[var(--brand-navy-deep)]">{item.why}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p data-testid="fm-concerns-footnote" className="mt-5 text-sm text-[var(--brand-muted)]">
              These are examples of concerns to discuss, not a diagnostic checklist.
            </p>
            <button
              data-testid="fm-concerns-request-button"
              onClick={onRequest}
              className="mt-6 inline-flex items-center justify-center bg-[var(--brand-navy)] text-white text-base font-semibold px-8 py-3.5 hover:bg-[var(--brand-navy-deep)] transition-colors"
            >
              Request an Appointment
            </button>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
